import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type CSSProperties } from 'react';
import { cn } from './cn';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-medium transition-colors',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: '',
        cta: '',
        secondary: 'border',
        outline: 'border',
        ghost: '',
        danger: 'border',
      },
      size: {
        sm: 'h-7 px-2.5 text-[13px]',
        md: 'h-8 px-3.5 text-[13px]',
        lg: 'h-10 px-5 text-[14px]',
      },
      density: {
        compact: 'h-7 px-2.5 text-[13px]',
        default: '',
      },
    },
    /* density override layers on top of size when set; default keeps size behavior */
    compoundVariants: [
      { density: 'compact', size: 'md', class: 'h-7 px-2.5 text-[13px]' },
      { density: 'compact', size: 'lg', class: 'h-8 px-3.5 text-[13px]' },
    ],
    defaultVariants: { variant: 'primary', size: 'md', density: 'default' },
  },
);

type Variant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

const VARIANT_STYLES: Record<Variant, CSSProperties> = {
  primary: {
    background: 'var(--cta-primary)',
    color: 'var(--cta-primary-on)',
  },
  cta: {
    background: 'var(--cta-primary)',
    color: 'var(--cta-primary-on)',
  },
  secondary: {
    background: 'var(--surface-0)',
    color: 'var(--content-strong)',
    borderColor: 'var(--divider-strong)',
  },
  outline: {
    background: 'var(--surface-0)',
    color: 'var(--content-strong)',
    borderColor: 'var(--divider-strong)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--content-default)',
  },
  danger: {
    background: 'var(--surface-raised)',
    color: 'var(--signal-danger)',
    borderColor: 'var(--signal-danger)',
  },
};

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, density, asChild, style, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const resolvedVariant = (variant ?? 'primary') as Variant;
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, density }), className)}
        style={{ borderRadius: 'var(--radius-button)', ...VARIANT_STYLES[resolvedVariant], ...style }}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
